import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import create from 'zustand';
import clientConfig from '../../helpers/clientConfig';
import { getKcToken, ensureKcToken } from '../../keycloak';
import logger from '../../utils/logger';
import { classifyMediaError } from '../../utils/mediaReadiness';

// Socket.IO URL'den path kısmını temizle — path namespace olarak yorumlanır
function getSocketOrigin(rawUrl) {
    if (!rawUrl) return '';
    try {
        const u = new URL(rawUrl);
        return u.origin;          // "https://api.dev.arvis.com.tr"
    } catch {
        return rawUrl;            // fallback: değişiklik yapma
    }
}

const SOCKET_URL = getSocketOrigin(clientConfig.REACT_APP_SOCKET_URL || clientConfig.REACT_APP_API_BASE_URL);

// Video kalitesi ayarlarını döndüren yardımcı fonksiyon
function getVideoConstraintsByQuality(quality = 'high', devicePerformance = 'medium') {
    const constraints = {
        low: {
            width: { ideal: 320 },
            height: { ideal: 240 },
            frameRate: { max: 15 }
        },
        medium: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 24 }
        },
        high: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
        },
        ultra: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 }
        }
    };

    // Cihaz performansına göre kaliteyi ayarla
    if (quality === 'auto') {
        if (devicePerformance === 'low') {
            return constraints.low;
        } else if (devicePerformance === 'medium') {
            return constraints.medium;
        } else {
            return constraints.high;
        }
    }

    return constraints[quality] || constraints.medium;
}

/** Env'den gelen virgüllü URL listesini temizler; undefined elemanları düşürür */
function parseCommaSeparatedUrls(value) {
    if (!value || typeof value !== 'string') return [];
    return value.split(',')
        .map((s) => s.trim())
        .filter(u => u && u !== 'undefined' && u !== 'null' && u !== '');
}

/** RTCPeerConnection için iceServers: TURN env boşken [undefined] oluşmasını engeller */
function buildIceServers() {
    const servers = [];

    // STUN sunucuları
    const stunUrls = parseCommaSeparatedUrls(clientConfig.REACT_APP_STUN_SERVERS);
    if (stunUrls.length > 0) {
        servers.push({ urls: stunUrls });
    } else {
        servers.push({ urls: ['stun:stun.l.google.com:19302'] });
    }

    // H5: dual TURN servers — browser failover when primary unreachable.
    [1, 2].forEach((n) => {
        const turnUrls = parseCommaSeparatedUrls(clientConfig[`REACT_APP_TURN_SERVER_${n}_URLS`]);
        if (turnUrls.length === 0) return;
        const username = clientConfig[`REACT_APP_TURN_SERVER_${n}_USERNAME`];
        const credential = clientConfig[`REACT_APP_TURN_SERVER_${n}_CREDENTIAL`];
        if (username && credential) {
            servers.push({ urls: turnUrls, username, credential });
        } else {
            servers.push({ urls: turnUrls });
        }
    });

    // Final güvenlik: urls dizisi boş veya undefined olan girişleri temizle
    return servers.filter(s => s && s.urls && (Array.isArray(s.urls) ? s.urls.length > 0 : !!s.urls));
}

const createConnectionSlice = (set, get) => ({
    // Bağlantı durumu state'leri
    socket: null,
    connected: false,
    error: null,
    beforeUnloadHandler: null,

    // ✅ Profesyonel güncelleme yönetimi için yeni state'ler
    lastUpdateTime: null,
    isUpdating: false,
    updateInterval: null,
    manualUpdateCount: 0,

    // Müşteri sıra pozisyonu state'leri
    customerQueuePosition: null,
    totalWaitingCustomers: 0,
    estimatedWaitTime: null,
    lastQueueUpdate: null,

    // Müşteri listesini yenileme - Profesyonel versiyon
    refreshCustomerList: (force = false) => {
        const { socket, lastUpdateTime, isUpdating, manualUpdateCount } = get();
        const userType = get().userType; // userSlice'dan al
        /* 
                console.log('🔄 refreshCustomerList çağrıldı:', {
                    force,
                    socket: !!socket,
                    socketConnected: socket?.connected,
                    userType,
                    isUpdating,
                    manualUpdateCount
                }); */

        if (!socket || !socket.connected) {
            console.warn('❌ Socket bağlantısı yok, müşteri listesi yenilenemiyor');
            return;
        }

        if (userType !== 'agent') {
            console.warn('❌ Sadece agentlar müşteri listesini yenileyebilir. userType:', userType);
            return;
        }

        // ✅ Debounce kontrolü - son 2 saniye içinde güncelleme yapıldıysa bekle
        const now = Date.now();
        if (!force && lastUpdateTime && (now - lastUpdateTime) < 2000) {
            return;
        }

        // ✅ Zaten güncelleme yapılıyorsa bekle
        if (isUpdating && !force) {
            return;
        }


        // Güncelleme durumunu işaretle
        set({ isUpdating: true });

        // Sunucudan güncel müşteri listesini iste
        socket.emit('request-customer-list');

        // ✅ Sadece manuel güncellemelerde toast göster
        if (force) {
            /*             toast('Müşteri listesi güncelleniyor...', {
                            icon: '🔄',
                            duration: 1500
                        }); */
        }

        // Güncelleme zamanını kaydet
        set({
            lastUpdateTime: now,
            manualUpdateCount: force ? manualUpdateCount + 1 : manualUpdateCount
        });

        // 3 saniye sonra güncelleme durumunu sıfırla
        setTimeout(() => {
            set({ isUpdating: false });
        }, 3000);
    },

    // ✅ Gelişmiş otomatik güncelleme sistemi
    // H3: Module-level lock guards against concurrent calls from rapid
    // disconnect/reconnect cycles spawning multiple intervals (self-DDoS).
    // _refreshLock is captured in closure across calls to startAutoRefresh.
    startAutoRefresh: () => {
        if (createConnectionSlice._refreshLock) {
            // Another call is mid-setup — bail out, that one will install the interval.
            return;
        }
        createConnectionSlice._refreshLock = true;
        try {
            const { updateInterval } = get();
            // Always clear before set — even if updateInterval looks stale.
            if (updateInterval) {
                clearInterval(updateInterval);
            }
            const interval = setInterval(() => {
                const { socket, connected, userType, isUpdating } = get();
                if (socket && connected && userType === 'agent' && !isUpdating) {
                    socket.emit('request-customer-list');
                }
            }, 10000);
            set({ updateInterval: interval });
        } finally {
            createConnectionSlice._refreshLock = false;
        }
    },

    // ✅ Görüşme sonrası otomatik yenileme
    refreshAfterCall: () => {
        const { socket, connected, userType } = get();

        if (socket && connected && userType === 'agent') {

            // Hemen yenile
            socket.emit('request-customer-list');

            // 2 saniye sonra tekrar yenile (güvenlik için)
            setTimeout(() => {
                if (socket && connected) {
                    socket.emit('request-customer-list');
                }
            }, 2000);

            // 5 saniye sonra tekrar yenile
            setTimeout(() => {
                if (socket && connected) {
                    socket.emit('request-customer-list');
                }
            }, 5000);
        }
    },

    // ✅ Manuel yenileme - geliştirilmiş versiyon
    manualRefresh: () => {
        const { socket, connected, userType, isUpdating } = get();

        if (!socket || !connected) {
            toast.error('Sunucuya bağlantı kurulamadı');
            return;
        }

        if (userType !== 'agent') {
            console.warn('Sadece agentlar müşteri listesini yenileyebilir');
            return;
        }

        if (isUpdating) {
            return;
        }


        set({
            isUpdating: true,
            lastUpdateTime: new Date(),
            manualUpdateCount: get().manualUpdateCount + 1
        });

        socket.emit('request-customer-list');

        // 3 saniye sonra güncelleme durumunu sıfırla
        setTimeout(() => {
            set({ isUpdating: false });
        }, 3000);
    },

    // Mesaj gönderme fonksiyonu
    sendMessage: (messageText) => {
        const { socket, activeCall } = get();
        if (!socket || !socket.connected) {
            console.warn('❌ Mesaj gönderilemedi: Socket bağlantısı yok');
            return;
        }

        if (!messageText || messageText.trim() === '') return;

        socket.emit('send-message', {
            text: messageText,
            timestamp: new Date().toISOString(),
            // Eğer aktif bir görüşme varsa onun verilerini de ekleyebiliriz
            callId: activeCall?.id,
            toId: activeCall?.customerId
        });

        // Chat state'ini burada güncellemek yerine socket'ten gelen 'message-received'i bekleyebiliriz
        // veya optimist bir güncelleme yapabiliriz.
    },

    // WebRTC kurulumunu başlat
    setupWebRTC: async (peerId) => {
        try {
            const { socket } = get();


            if (!socket || !socket.connected) {
                toast.error('Sunucuya bağlantı kurulamadı');
                return;
            }

            const isAgent = get().userType === 'agent';

            // Medya akışı izinleri al
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: getVideoConstraintsByQuality('high', 'high'),
                    audio: true
                });
            } catch (mediaError) {
                // Oturum sunucuda açıldıktan sonra temsilcinin cihazı açılamadı: görüşmeyi
                // agent_media_failed ile kapat (müşteri kuyruğun başına döner). Müşteri yolu eski davranışta kalır.
                if (isAgent) {
                    logger.error('WebRTC kurulumu: temsilci kamera/mikrofon açılamadı:', mediaError);
                    get().abortCallForAgentMediaFailure(classifyMediaError(mediaError));
                    return;
                }
                throw mediaError;
            }

            // getUserMedia beklerken görüşme bittiyse (karşı taraf kapattı) akışı açık bırakma.
            if (isAgent && !['connecting', 'connected'].includes(get().callStatus)) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }

            // State'e medya akışını kaydet
            set({ localStream: stream });

            // Görüşme sırasında cihaz çıkarılırsa/izin kalkarsa track 'ended' olur (stop() bunu tetiklemez).
            if (isAgent) {
                stream.getTracks().forEach((track) => {
                    track.addEventListener('ended', () => {
                        if (get().localStream !== stream) return;
                        get().abortCallForAgentMediaFailure('NotFoundError');
                    });
                });
            }

            // RTCPeerConnection oluştur
            const peerConnection = new RTCPeerConnection({
                iceServers: buildIceServers(),
                iceCandidatePoolSize: 10,
                iceTransportPolicy: 'all',
                bundlePolicy: 'max-bundle',
                rtcpMuxPolicy: 'require'
            });

            // Yerel medya akışını peer connection'a ekle
            stream.getTracks().forEach(track => {
                peerConnection.addTrack(track, stream);
            });

            // Uzak medya akışını yakala
            peerConnection.ontrack = (event) => {
                set({ remoteStream: event.streams[0] });
                set({ callStatus: 'connected' });
            };

            // ICE adaylarını yakala ve gönder
            peerConnection.onicecandidate = (event) => {
                if (event.candidate) {
                    socket.emit('webrtc-signal', {
                        to: peerId,
                        signal: {
                            type: 'ice-candidate',
                            candidate: event.candidate
                        }
                    });
                }
            };

            // H2: ICE failure recovery — one restart attempt, then surface to UI.
            let _iceRestartAttempted = false;
            peerConnection._iceFailTimer = null;
            const _iceTimerRef = { timer: null };

            peerConnection.oniceconnectionstatechange = () => {
                const s = peerConnection.iceConnectionState;
                if (s === 'connected' || s === 'completed') {
                    if (_iceTimerRef.timer) { clearTimeout(_iceTimerRef.timer); _iceTimerRef.timer = null; }
                    return;
                }
                if (s === 'failed' && !_iceRestartAttempted) {
                    _iceRestartAttempted = true;
                    console.warn('[ICE] state=failed → restartIce()');
                    try { peerConnection.restartIce(); } catch (e) { console.warn('restartIce error', e); }
                    _iceTimerRef.timer = setTimeout(() => {
                        if (peerConnection.iceConnectionState !== 'connected' &&
                            peerConnection.iceConnectionState !== 'completed') {
                            logger.error('[ICE] restartIce timeout — surfacing to UI');
                            set({ callStatus: 'ice-failed', error: 'Görüntülü bağlantı kurulamadı (ICE failed). Lütfen tekrar deneyin.' });
                            try { toast.error('Görüntülü bağlantı kurulamadı, lütfen tekrar deneyin.'); } catch {}
                        }
                    }, 10000);
                }
            };
            peerConnection._iceTimerRef = _iceTimerRef;

            // Sunucu ilk 'connected' anını media_connected_at olarak yazar (konuşma süresi, OD-8).
            if (isAgent) {
                peerConnection.onconnectionstatechange = () => {
                    const state = peerConnection.connectionState;
                    if (state === 'connected' || state === 'failed' || state === 'disconnected') {
                        socket.emit('webrtc-connection-state', {
                            state,
                            sessionId: get().activeCall?.sessionId || get().sessionId,
                            to: peerId
                        });
                    }
                };
            }

            // Eğer başlatan tarafsak, teklif oluştur ve gönder
            if (get().userType === 'agent') {
                const offer = await peerConnection.createOffer();
                await peerConnection.setLocalDescription(offer);

                socket.emit('webrtc-signal', {
                    to: peerId,
                    signal: offer
                });
            }

            // State'e peer connection kaydet
            set({ peerConnection });

        } catch (error) {
            logger.error('WebRTC kurulumu sırasında hata:', error);
            toast.error('Video bağlantısı kurulamadı: ' + error.message);
        }
    },

    // Konferans state'leri
    participants: [],
    inviteToken: null,
    peerConnections: {}, // peerId -> RTCPeerConnection
    remoteStreams: {}, // peerId -> MediaStream

    // Davet oluştur (temsilci tarafı)
    createInvite: () => {
        const { socket } = get();
        if (!socket) return;
        socket.emit('create-invite');
    },

    // Davet ile katıl (guest tarafı)
    joinByInvite: (token) => {
        const { socket } = get();
        if (!socket) return;
        socket.emit('join-by-invite', { token });
    },

    // Belirli bir peer için konferans peer connection kur
    setupConferencePeer: async (peerId, isInitiator = false, sessionId = null) => {
        try {
            const state = get();
            const { socket } = state;
            if (!socket) return;

            // Eğer zaten varsa tekrar kurma
            if (state.peerConnections[peerId]) return state.peerConnections[peerId];

            // Yerel medya akışını hazırla
            let localStream = state.localStream;
            if (!localStream) {
                try {
                    localStream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                        video: getVideoConstraintsByQuality(state.videoQuality, state.devicePerformance)
                    });
                    set({ localStream });
                } catch (err) {
                    toast.error('Kamera/Mikrofon erişimi alınamadı');
                    throw err;
                }
            }

            // PeerConnection oluştur
            const pc = new RTCPeerConnection({
                iceServers: buildIceServers(),
                iceCandidatePoolSize: 8,
            });

            // Local trackleri ekle
            localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

            // Remote track geldiğinde kaydet
            pc.ontrack = (event) => {
                const stream = event.streams[0];
                set({ remoteStreams: { ...get().remoteStreams, [peerId]: stream } });
            };

            // ICE candidate gönder
            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    socket.emit('webrtc-signal', {
                        to: peerId,
                        signal: { type: 'ice-candidate', candidate: event.candidate },
                        sessionId: sessionId || get().sessionId || window.sessionStorage.getItem('current_session_id')
                    });
                }
            };

            // Kaydet
            set({ peerConnections: { ...get().peerConnections, [peerId]: pc } });

            // Başlatan taraf teklif yollar
            if (isInitiator) {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                socket.emit('webrtc-signal', {
                    to: peerId,
                    signal: offer,
                    sessionId: sessionId || get().sessionId || window.sessionStorage.getItem('current_session_id')
                });
            }

            return pc;
        } catch (error) {
            logger.error('Konferans peer kurulurken hata:', error);
        }
    },

    // Socket bağlantısını başlatan fonksiyon
    initSocket: async () => {
        // Eğer zaten bir soket varsa, yeni bir tane oluşturma
        if (get().socket) {
            return get().socket;
        }

        // FE-2: expired KC token ile bağlanılmasın — connect öncesi refresh dene.
        // Başarısız olursa (örn. KC kullanılmıyor / legacy auth) get().token fallback'ine güvenle devam edilir.
        await ensureKcToken(60).catch(() => null);

        const socket = io(SOCKET_URL, {
            auth: (cb) => {
                cb({ token: getKcToken() || get().token });
            },
            transports: ['websocket'],
            upgrade: false
        });

        const handleBeforeUnload = () => {
            if (socket && socket.connected && get().userType === 'agent') {
                socket.emit('agent-status-update', { status: 'offline' });
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        set({ beforeUnloadHandler: handleBeforeUnload });

        socket.on('connect', () => {
            set({ socket, connected: true });

            // Bağlantı kurulduğunda müşteri listesini otomatik olarak iste
            const userType = get().userType; // userSlice'dan al

            if (userType === 'agent') {
                setTimeout(() => {
                    // Masa başka yere geçtiyse otomatik yeniden kayıt yok — yoksa iki sekme birbirini atıp durur.
                    if (get().deskBlock) return;
                    console.log('✅ Agent için otomatik güncelleme başlatılıyor...');
                    // Re-register agent after reconnect so activeAgents map is repopulated
                    const userData = get().userData;
                    if (userData) {
                        get().registerUser('agent', { ...userData });
                    }
                    // Görüşme sırasında soket koptu ve geri geldi: sunucu oturumu grace süresince açık tutar,
                    // yeni soketi ancak bu olayla görüşmeye yeniden bağlar (sayfa yenilemede activeCall yok).
                    const { activeCall, callStatus } = get();
                    if (activeCall?.sessionId && (callStatus === 'connecting' || callStatus === 'connected')) {
                        socket.emit('join-session-room', { sessionId: activeCall.sessionId });
                    }
                    socket.emit('request-customer-list');
                    // ✅ Otomatik güncelleme başlat
                    get().startAutoRefresh();
                }, 500);
            } else {
            }
        });

        socket.on('disconnect', () => {
            set({ connected: false });
        });

        socket.on('error', (error) => {
            logger.error('Socket hatası:', error);
            toast.error(error.message || 'Bir hata oluştu');
            set({ error: error.message });
        });

        // KYC-169: request-connection sürecinde sunucu tarafı hata (oturum oluşturulamadı,
        // karşı taraf bağlantısı koptu vb.) — önceden hiçbir listener yoktu, kullanıcı
        // hatayı hiç göremiyordu. Agent'ı/müşteriyi bekleme listesine geri döndür.
        socket.on('connection-error', ({ message, code } = {}) => {
            logger.error('Bağlantı hatası (connection-error):', message);
            toast.error(message || 'Bağlantı kurulurken bir hata oluştu');
            set({ activeCall: null, callStatus: null });
            // Sunucu temsilciyi kamera/mikrofon hazır değil diye reddetti: durumu yeniden ölç ve bildir.
            if (code === 'AGENT_MEDIA_NOT_READY') {
                get().checkMediaReadiness();
            }
        });

        // Aynı temsilci başka bir yerden girdi: bu soket masadan çıkarıldı (sunucu ardından bağlantıyı keser).
        socket.on('agent-session-replaced', ({ message } = {}) => {
            get().blockAgentDesk('replaced', message || 'Hesabınızla başka bir yerden giriş yapıldı. Bu ekranda müşteri karşılanmıyor.');
        });

        // Bu sekmeden kayıt reddedildi: temsilcinin başka bir yerde süren görüşmesi var.
        socket.on('agent-session-rejected', ({ message } = {}) => {
            get().blockAgentDesk('rejected', message || 'Başka bir yerde süren görüşmeniz var. Görüşme bitince tekrar deneyin.');
        });

        // Sunucu register-agent'ı tamamladı: medya durumunu ancak şimdi bildirebiliriz (öncesi düşebilir).
        socket.on('agent-registered', () => {
            const { callStatus } = get();
            if (callStatus === 'connecting' || callStatus === 'connected') {
                get().reportMediaStatus();
            } else {
                get().checkMediaReadiness();
            }
        });

        // KYC-169: müşteri bağlantı isteğini reddettiğinde temsilciye bildirim — önceden dinlenmiyordu.
        socket.on('connection-rejected', ({ customerId, reason } = {}) => {
            logger.info('Bağlantı isteği reddedildi (connection-rejected):', { customerId, reason });
            toast.error(reason || 'Müşteri bağlantı isteğini reddetti');
            set({ activeCall: null, callStatus: null });
        });

        // KYC-169: register-customer sunucu tarafında başarısız olduğunda — önceden
        // dinlenmiyordu, müşteri kayıt hatasını hiç göremiyordu.
        socket.on('register-error', ({ code, message } = {}) => {
            logger.error('Müşteri kaydı hatası (register-error):', { code, message });
            toast.error(message || 'Kayıt sırasında bir hata oluştu');
        });

        // Temsilci listesi güncellemesi
        socket.on('agent-list-update', (agents) => {

            set({ agents });

            // Agent statü slice'ını da güncelle
            const { updateAgentList } = get();
            if (updateAgentList) {
                updateAgentList(agents);
            }
        });

        // Bekleyen müşteri bildirimi (temsilciler için)
        socket.on('customers-waiting', ({ count, customers }) => {
            set({ waitingCount: count, waitingCustomers: customers });

            // Bildirim kontrolü
            get().checkCustomerCountChange(count, customers);
        });

        // Müşteri listesi güncellemesi (request-customer-list event'ine response)
        socket.on('customer-list-updated', ({ count, customers }) => {
            set({
                waitingCount: count,
                waitingCustomers: customers,
                isUpdating: false,
                lastUpdateTime: new Date()
            });

            // Bildirim kontrolü
            get().checkCustomerCountChange(count, customers);
        });

        // Müşteri eklendi event'i
        socket.on('customer-added', ({ count, customers, newCustomer }) => {
            set({
                waitingCount: count,
                waitingCustomers: customers,
                isUpdating: false,
                lastUpdateTime: new Date()
            });

            // FE-172: KVKK ekranı girişinde (bu event, kabul öncesi de tetiklenir) ayrı bir
            // bildirim sesi çalınmasın — asıl bildirim, KVKK kabulü sonrası checkCustomerCountChange
            // üzerinden (customers-waiting event'i ile) tetiklenir.
            get().checkCustomerCountChange(count, customers);
        });

        // Müşteri kaldırıldı event'i
        socket.on('customer-removed', ({ count, customers }) => {
            set({
                waitingCount: count,
                waitingCustomers: customers,
                isUpdating: false,
                lastUpdateTime: new Date()
            });

            // Bildirim kontrolü
            get().checkCustomerCountChange(count, customers);
        });

        // Müşteri listesi değişikliği event'i (genel)
        socket.on('customer-list-changed', ({ count, customers }) => {
            set({
                waitingCount: count,
                waitingCustomers: customers,
                isUpdating: false,
                lastUpdateTime: new Date()
            });

            // Bildirim kontrolü
            get().checkCustomerCountChange(count, customers);
        });

        // Müşteri sıra pozisyonu güncellemesi
        socket.on('queue-position-updated', ({ position, totalWaiting, estimatedWaitTime }) => {
            set({
                customerQueuePosition: position,
                totalWaitingCustomers: totalWaiting,
                estimatedWaitTime: estimatedWaitTime,
                lastQueueUpdate: new Date()
            });
        });

        // Bağlantı kabul edildi (müşteriler için) - DÜZELTİLDİ ✅
        socket.on('connection-accepted', ({ agentId, agentName, sessionId }) => {
            set({
                activeCall: { ...get().activeCall, agentId, agentName, sessionId },
                callStatus: 'connecting',
                callEndEmitted: false,
                sessionId, // Ana sessionId'yi de kaydet
                currentSessionId: sessionId
            });
            // Oturum odasına katıl
            socket.emit('join-session-room', { sessionId });
            get().setupWebRTC(agentId);
        });

        // Agent statü güncelleme event'leri
        socket.on('agent-status-updated', (data) => {
            if (data.success) {
                const { setCurrentStatus } = get();
                setCurrentStatus(data.data);
                toast.success('Statü başarıyla güncellendi');
            } else {
                toast.error(data.error || 'Statü güncellenemedi');
            }
        });

        socket.on('agent-status-history', (data) => {
            if (data.success) {
                const { setStatusHistory } = get();
                setStatusHistory(data.data);
            } else {
                toast.error(data.error || 'Statü geçmişi alınamadı');
            }
        });

        // Bağlantı kuruldu (temsilciler için) - DÜZELTİLDİ ✅
        socket.on('connection-established', ({ customerId, customerSocketId, customerName, idNumber, issue, sessionId }) => {

            // SessionId ve customerId değerlerini localStorage ve sessionStorage'a kaydet
            if (sessionId) {
                window.localStorage.setItem('last_session_id', sessionId);
                window.sessionStorage.setItem('current_session_id', sessionId);
                // Store'a da kaydet
                localStorage.setItem('currentCallSessionId', sessionId);
            }

            if (customerId) {
                window.localStorage.setItem('last_customer_id', customerId);
                window.sessionStorage.setItem('current_customer_id', customerId);
            }

            if (customerSocketId) {
                window.localStorage.setItem('last_customer_socket_id', customerSocketId);
                window.sessionStorage.setItem('current_customer_socket_id', customerSocketId);
            }

            // ✅ Yeni görüşme başladığında kimlik verilerini temizle

            set({
                activeCall: { ...get().activeCall, customerId, customerSocketId, customerName, idNumber, issue, sessionId },
                callStatus: 'connecting',
                // FE-143 bayrağı görüşme başına: sıfırlanmazsa sonraki görüşmelerde end-call hiç gönderilmiyordu.
                callEndEmitted: false,
                sessionId, // Ana sessionId'yi de kaydet
                currentSessionId: sessionId
            });

            // Oturum odasına katıl
            socket.emit('join-session-room', { sessionId });
            get().setupWebRTC(customerSocketId); // WebRTC için socket ID kullan
        });

        // Mobile SDK: ConnectActivity geç bağlandığında sunucu bu eventi gönderir.
        // Agent mevcut peerConnection'ı kapatıp yeni offer oluşturmalı —
        // sunucu artık sinyali doğrudan ConnectActivity soketine iletir (alias yok).
        socket.on('customer-ready-for-webrtc', ({ customerSocketId: newCustomerSocketId, sessionId }) => {
            console.log('[WebRTC] ConnectActivity hazır, WebRTC yeniden başlatılıyor...', { newCustomerSocketId, sessionId });
            // Sunucudan gelen yeni ConnectActivity socket ID'sini kullan (varsa).
            // Yoksa eski activeCall.customerSocketId'yi fallback olarak kullan.
            const targetSocketId = newCustomerSocketId || get().activeCall?.customerSocketId;
            if (!targetSocketId) return;
            // activeCall'u yeni socket ID ile güncelle — sonraki sinyaller doğru yere gitsin.
            if (newCustomerSocketId) {
                set(state => ({
                    activeCall: state.activeCall
                        ? { ...state.activeCall, customerSocketId: newCustomerSocketId }
                        : null
                }));
            }
            // Eski bağlantıyı kapat
            const prevPc = get().peerConnection;
            if (prevPc) {
                if (prevPc._iceTimerRef?.timer) {
                    clearTimeout(prevPc._iceTimerRef.timer);
                    prevPc._iceTimerRef.timer = null;
                }
                try { prevPc.close(); } catch (_) {}
                set({ peerConnection: null, remoteStream: null });
            }
            // Yeni offer oluştur — doğrudan ConnectActivity soketine gidecek.
            get().setupWebRTC(targetSocketId);
        });

        // WebRTC sinyal mesajları (konferans + 1:1)
        socket.on('webrtc-signal', async ({ signal, from, sessionId }) => {
            try {
                // 1:1 akış: mevcut tekil peerConnection varsa önce onu işle
                const directPC = get().peerConnection;
                if (directPC && (!get().peerConnections[from])) {
                    if (signal.type === 'offer') {
                        await directPC.setRemoteDescription(new RTCSessionDescription(signal));
                        const answer = await directPC.createAnswer();
                        await directPC.setLocalDescription(answer);
                        socket.emit('webrtc-signal', { to: from, signal: answer, sessionId });
                    } else if (signal.type === 'answer') {
                        await directPC.setRemoteDescription(new RTCSessionDescription(signal));
                    } else if (signal.type === 'ice-candidate') {
                        if (signal.candidate && (signal.candidate.candidate || signal.candidate.sdpMid || signal.candidate.sdpMLineIndex !== undefined)) {
                            try {
                                // Bazı tarayıcılar null candidate'i "end-of-candidates" olarak kabul eder ancak constructor hata verebilir
                                const candidate = new RTCIceCandidate(signal.candidate);
                                await directPC.addIceCandidate(candidate);
                            } catch (e) {
                                console.warn('ICE Candidate eklenemedi (Format hatası):', e);
                            }
                        } else if (!signal.candidate || signal.candidate.candidate === null) {
                            // End-of-candidates sinyali
                            try {
                                await directPC.addIceCandidate(null);
                            } catch (e) { /* ignore end-of-candidate errors */ }
                        }
                    }
                    return;
                }

                // Önce konferans peer var mı bak
                let pc = get().peerConnections[from];
                if (!pc) {
                    // 1:1 akışta olmayabilir, konferans için oluştur
                    pc = await get().setupConferencePeer(from, false, sessionId);
                }

                if (!pc) return;

                if (signal.type === 'offer') {
                    await pc.setRemoteDescription(new RTCSessionDescription(signal));
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    socket.emit('webrtc-signal', { to: from, signal: answer, sessionId });
                } else if (signal.type === 'answer') {
                    await pc.setRemoteDescription(new RTCSessionDescription(signal));
                } else if (signal.type === 'ice-candidate') {
                    if (signal.candidate) {
                        try {
                            await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
                        } catch (e) {
                            console.warn('ICE Candidate eklenemedi:', e);
                        }
                    }
                }
            } catch (error) {
                logger.error('WebRTC sinyali işlenemedi:', error);
            }
        });

        // Görüşme sonlandırıldı
        socket.on('call-ended', () => {
            get().endCall();
        });

        // Kopup geri gelen soket, grace süresi dolmuş (kapanmış) bir oturuma yeniden katılmak istedi.
        // Yalnız şu anki görüşmenin oturumuysa yerelde bitir; eski oturum (ör. sonuç ekranındaki notlar) yok sayılır.
        socket.on('session-closed', ({ sessionId } = {}) => {
            const { activeCall, callStatus } = get();
            if (!sessionId || activeCall?.sessionId !== sessionId) return;
            if (callStatus !== 'connecting' && callStatus !== 'connected') return;
            get().endCall();
        });

        // KYC-169: sunucunun end-call'ı işlediğini bu soketin kendisine onaylaması —
        // önceden dinlenmiyordu. Local state (callSlice.js endCall/cleanEndCall) end-call
        // emit edilirken zaten iyimser olarak güncelleniyor; bu yalnızca kullanıcıya
        // sunucu tarafı onayını görünür kılıyor.
        socket.on('call-ended-confirmation', () => {
            toast.success('Görüşme sonlandırma işlemi sunucu tarafından onaylandı');
        });

        // Yeni mesaj
        socket.on('new-message', (message) => {
            set({ messages: [...get().messages, message] });
        });

        // Davet oluşturma sonucu
        socket.on('invite-created', (payload) => {
            if (payload?.success) {
                set({ inviteToken: payload.token });
            }
        });

        // Davet ile katılım sonucu
        socket.on('join-by-invite-result', (payload) => {
            if (!payload?.success) return;
            // İlk katılımcı listesi 'participants' event'i ile gelecek
        });

        // Konferans katılımcıları - DÜZELTİLDİ ✅
        socket.on('participants', ({ sessionId, participants }) => {
            set({
                participants: participants || [],
                sessionId, // Ana sessionId'yi de kaydet
                currentSessionId: sessionId
            });

            if (sessionId) {
                set({ currentSessionId: sessionId });
                localStorage.setItem('currentCallSessionId', sessionId);
            }
        });

        // Bir katılımcı odaya katıldı
        socket.on('participant-joined', async ({ socketId }) => {
            const { participants } = get();
            if (!participants.includes(socketId)) {
                set({ participants: [...participants, socketId] });
            }
            const myId = socket.id;
            const isInitiator = myId && myId.localeCompare(socketId) > 0;
            await get().setupConferencePeer(socketId, isInitiator);
        });

        // Bir katılımcı odadan ayrıldı
        socket.on('participant-left', ({ socketId }) => {
            const { participants, peerConnections, remoteStreams } = get();
            set({ participants: participants.filter(id => id !== socketId) });
            const pc = peerConnections[socketId];
            if (pc) {
                try { pc.close(); } catch { }
                const newPC = { ...peerConnections }; delete newPC[socketId];
                set({ peerConnections: newPC });
            }
            if (remoteStreams[socketId]) {
                const ns = { ...remoteStreams }; delete ns[socketId];
                set({ remoteStreams: ns });
            }
        });

        set({ socket });
        return socket;
    },

    // Temsilci masası başka bir yerde (owner kararı 2026-10-14: bir temsilci aynı anda tek yerden müşteri
    // alır, son giriş kazanır). null | { kind: 'replaced' | 'rejected', message }
    deskBlock: null,

    // Masa başka sekmeye/cihaza geçtiğinde ya da orada görüşme sürdüğü için reddedildiğinde: bu ekran
    // müşteri almaz, liste yenilemesi ve cihaz izlemesi durur, otomatik yeniden kayıt yapılmaz.
    blockAgentDesk: (kind, message) => {
        const { updateInterval } = get();
        if (updateInterval) clearInterval(updateInterval);
        get().stopMediaMonitoring();
        set({ deskBlock: { kind, message }, updateInterval: null });
    },

    // Kullanıcı "Bu ekranda devam et" / "Tekrar dene" dedi: masayı bu ekrana al (son giriş kazanır).
    reclaimAgentDesk: () => {
        const { socket, deskBlock, userData } = get();
        if (!socket || !deskBlock) return;
        set({ deskBlock: null });
        if (!socket.connected) {
            // 'connect' handler'ı yeniden kaydı ve liste yenilemeyi yapar.
            socket.connect();
            return;
        }
        if (userData) get().registerUser('agent', { ...userData });
        socket.emit('request-customer-list');
        get().startAutoRefresh();
    },

    // Bağlantıyı ve kaynakları temizleyen fonksiyon
    cleanup: () => {
        const { socket, localStream, updateInterval, peerConnection, beforeUnloadHandler } = get();

        if (beforeUnloadHandler) {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
        }

        // ✅ Otomatik güncellemeyi durdur
        if (updateInterval) {
            clearInterval(updateInterval);
        }

        // Mevcut medya akışlarını kapat
        if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
        }

        // Peer connection ve timer temizle
        if (peerConnection) {
            if (peerConnection._iceTimerRef?.timer) {
                clearTimeout(peerConnection._iceTimerRef.timer);
                peerConnection._iceTimerRef.timer = null;
            }
            try { peerConnection.close(); } catch (_) {}
        }

        // Socket bağlantısını kapat
        if (socket) {
            if (socket.connected && get().userType === 'agent') {
                socket.emit('agent-status-update', { status: 'offline' });
            }
            socket.disconnect();
        }

        // ✅ Tüm state'leri sıfırla
        set({
            socket: null,
            connected: false,
            localStream: null,
            remoteStream: null,
            peerConnection: null,
            activeCall: null,
            callStatus: null,
            messages: [],
            // Recording state'lerini de temizle
            recordedVideo: null,
            localRecordedVideo: null,
            isRecording: false,
            isLocalRecording: false,
            // ✅ Güncelleme state'lerini de temizle
            lastUpdateTime: null,
            isUpdating: false,
            updateInterval: null,
            manualUpdateCount: 0,
            deskBlock: null
        });
    },

    // Görüşme başlat
    startCall: (agentId, sessionId) => set((state) => {

        const newState = {
            activeCall: {
                agentId,
                sessionId,
                startTime: new Date()
            },
            callStatus: 'connected',
            currentSessionId: sessionId
        };


        // Session ID'yi localStorage'a kaydet
        if (sessionId) {
            localStorage.setItem('currentCallSessionId', sessionId);
        }

        return newState;
    }),

    // Görüşme sonlandır - DÜZELTİLDİ ✅
    endCall: () => set((state) => {
        /*         console.log('🔚 connectionSlice endCall çağrıldı, mevcut durum:', {
                    callStatus: state.callStatus,
                    activeCall: state.activeCall,
                    currentSessionId: state.currentSessionId
                }); */

        // Peer connection ve timer temizle
        const prevPc = state.peerConnection;
        if (prevPc) {
            if (prevPc._iceTimerRef?.timer) {
                clearTimeout(prevPc._iceTimerRef.timer);
                prevPc._iceTimerRef.timer = null;
            }
            try { prevPc.close(); } catch (_) {}
        }

        const currentCall = state.activeCall;
        let sessionId = currentCall?.sessionId || state.currentSessionId;

        // Eğer hala yoksa localStorage'dan al
        if (!sessionId) {
            sessionId = localStorage.getItem('currentCallSessionId');
        }


        const newState = {
            activeCall: null,
            callStatus: 'ended',
            peerConnection: null,
            remoteStream: null,
            lastSessionId: sessionId,
            currentSessionId: null,
            lastCall: {
                sessionId: sessionId,
                endTime: new Date()
            }
        };


        // Session ID'yi localStorage'a kaydet (anket için)
        if (sessionId) {
            // Eski sessionId'leri temizle
            localStorage.removeItem('lastCallSessionId');
            localStorage.removeItem('currentCallSessionId');

            // Yeni sessionId'yi kaydet
            localStorage.setItem('currentCallSessionId', sessionId);
        }

        return newState;
    }),

    // Session ID yönetimi için yeni state - DÜZELTİLDİ ✅
    currentSessionId: null,
    lastSessionId: null,
    sessionId: null, // Ana sessionId state'i eklendi

    // Session ID set et
    setCurrentSessionId: (sessionId) => set((state) => {
        return {
            currentSessionId: sessionId,
            lastSessionId: state.currentSessionId,
            sessionId: sessionId // Ana sessionId'yi de güncelle
        };
    }),

    // Ana sessionId set et
    setSessionId: (sessionId) => set((state) => {
        return {
            sessionId: sessionId,
            currentSessionId: sessionId
        };
    }),
});

export default createConnectionSlice; 
